import { useMemo } from 'react'
import { createEntityHooks } from '../core/createEntityHooks'
import { useRxDB } from '../core/useRxDB'
import { deleteAuditFields } from '../core/utils'
import { useStatuses } from './statuses'
import { useMembers } from './members'
import type { BaseDocument } from '../core/types'
import useLoginStore from '@/store/LoginStore'

/**
 * Task document interface
 * Note: Description fields moved to TaskContent
 */
export interface TaskDocument extends BaseDocument {
  name: string
  // Description fields moved to TaskContent
  projectId?: string | null
  seqNo?: number | null
  identifier?: string | null
  statusId: string
  userId?: string | null
  accountableUserId?: string | null
  priority?: number | null
  dueDate?: number | null
  dueDateType?: 'date' | 'datetime' | null
  startDate?: number | null
  startDateType?: 'date' | 'datetime' | null
  isAllDay: boolean
  sortOrder?: string | null
  estimation?: number | null
  isSomeday: boolean
  isArchived: boolean
  parentId?: string | null
  workItemTypeId?: string | null
  coverImageUrl?: string | null
  coverImageType?: 'unsplash' | 'custom' | null
  coverImageMeta?: string | null
  statusName?: string
  statusColor?: string
  userName?: string | null
  userEmail?: string | null
  accountableUserName?: string | null
  accountableUserEmail?: string | null
  createdOn: number
  updatedOn: number
  deletedOn?: number | null
  archivedOn?: number | null
  archivedBy?: string | null
  aiGate?: 'plan_first' | 'direct' | null
}

/**
 * Enriched Task with fresh status and user data from joins
 */
export interface EnrichedTask extends TaskDocument {
  status?: {
    id: string
    name: string
    progressType: string
  }
  user?: {
    id: string
    name: string | null
    email: string | null
  }
  accountableUser?: {
    id: string
    name: string | null
    email: string | null
  }
}

/**
 * Task-specific hooks
 */
const hooks = createEntityHooks<TaskDocument>('tasks')

/**
 * Get tasks with fresh status and user data joined from their collections.
 * Falls back to denormalized fields if entities not found (offline resilience).
 */
export const useTasks = (
  filter?: Partial<TaskDocument>,
  options?: { enabled?: boolean },
) => {
  const { data, isLoading, db } = hooks.useEntities(filter, options)
  const { statuses } = useStatuses()
  const { members } = useMembers()

  const enrichedTasks = useMemo<Array<EnrichedTask>>(() => {
    if (!data) return []

    return data.map((task) => {
      const status = statuses?.find((s) => s.id === task.statusId && !s.isDeleted)
      const user = task.userId ? members?.find((m) => m.id === task.userId && !m.isDeleted) : undefined
      const accountableUser = task.accountableUserId ? members?.find((m) => m.id === task.accountableUserId && !m.isDeleted) : undefined

      return {
        ...task,
        status: status ? {
          id: status.id,
          name: status.name,
          progressType: status.progressType,
        } : undefined,
        user: user ? {
          id: user.id,
          name: user.name,
          email: user.email,
        } : undefined,
        accountableUser: accountableUser ? {
          id: accountableUser.id,
          name: accountableUser.name,
          email: accountableUser.email,
        } : undefined,
        // Keep denormalized fields as fallback for offline scenarios
      }
    })
  }, [data, statuses, members])

  return { tasks: enrichedTasks, isLoading, db }
}

// Wrapper to match old API (returns 'task' instead of 'data')
export const useTask = (id: string | undefined) => {
  const { data, isLoading } = hooks.useEntity(id)
  return { task: data, isLoading }
}

// Wrapper to provide entity-specific method names
export const useTaskMutations = () => {
  const { create, update, delete: deleteEntity } = hooks.useMutations()
  const { db } = useRxDB()
  return {
    createTask: create,
    updateTask: update,
    deleteTask: deleteEntity,
    archiveTask: async (id: string) => {
      const userId = useLoginStore.getState().userId
      await update(id, {
        isArchived: true,
        archivedOn: Date.now(),
        archivedBy: userId,
      })
      // Drop the favorite when the task is archived so it falls out of the
      // FAVORITES sidebar immediately (the server cascade handles other users).
      if (db && userId) {
        const starredDocs = await (db as any).starred
          .find({ selector: { entityType: 'task', entityId: id, isDeleted: false } })
          .exec()
        await Promise.all(
          starredDocs.map((doc: any) => doc.update({ $set: deleteAuditFields(userId) })),
        )
      }
    },
    unarchiveTask: async (id: string) => {
      await update(id, {
        isArchived: false,
        archivedOn: null,
        archivedBy: null,
      })
    },
  }
}
